<template>
  <div>
    <public-announcement :is-fixed="true" />
    <invitation-notifications v-if="currentUser.id" />
    <edu-yearly-notification v-if="currentUser.id" />
    <div class="my-plan">
      <OnboardingV3BackArrow />
      <page-breadcrumb
        :category="$i18nPresenter.t('account')"
        :text="breadCumbText"
      />
      <div class="my-plan-content">
        <div class="flex justify-center mb-6" v-if="isPresenterPlanLoading">
          <aha-spinner />
        </div>
        <div class="mb-6" v-if="hasErrorOnLoadingPresenterPlan">
          <aha-section>
            <template #header>
              <h5>{{ $i18nPresenter.t('Current Plan') }}</h5>
            </template>
            <template #body>
              <div class="flex flex-col items-center">
                <p>{{ $i18nPresenter.t("Cannot load your plan's details. Please try again by reloading this page.") }}</p>
                <ui-messages type="problemOccurs" />
              </div>
            </template>
          </aha-section>
        </div>

        <div v-if="$store.state.plan.type != 'free' && data.oneTime">
          <aha-section>
            <template #header>
              <h5>{{ $i18nPresenter.t('Current Plan') }}</h5>
            </template>
            <template #body>
              <table class="table-plan">
                <colgroup>
                  <col class="w-1/2 md:w-1/3">
                  <col class="w-1/2 md:w-2/3">
                </colgroup>
                <tbody class="w-full">
                  <tr v-if="!data.subscription">
                    <td colspan="2" class="py-2 italic">
                      {{ $i18nPresenter.t('You can upgrade to a Monthly or Yearly plan to use AhaSlides for more than one event (and save on cost!)') }}
                    </td>
                  </tr>
                  <tr>
                    <td class="table-plan-property">
                      <i class="mr-1 fas fa-cart-arrow-down fa-fw" />
                      {{ $i18nPresenter.t('Your plan:') }}
                    </td>
                    <td
                      class="py-2 font-bold table-plan-value"
                      :class="getPlanColor($store.state.plan)"
                    >
                      {{ $store.state.plan.name }}
                    </td>
                  </tr>
                  <tr>
                    <td class="table-plan-property">
                      <i class="mr-1 fas fa-user fa-fw" />
                      {{ $i18nPresenter.t('Owner:') }}
                    </td>
                    <td class="py-2 table-plan-value">
                      {{ currentUser.email }}
                    </td>
                  </tr>
                  <tr>
                    <td class="table-plan-property">
                      <i class="mr-1 fas fa-users fa-fw" />
                      {{ $i18nPresenter.t('Participant limit:') }}
                    </td>
                    <td class="py-2 table-plan-value plan-limit">
                      {{ $store.state.plan.limit | formatNumber }}
                    </td>
                  </tr>
                  <tr>
                    <td class="align-top table-plan-property">
                      <i class="mr-1 far fa-calendar-alt fa-fw" />
                      {{ $i18nPresenter.t('Event date:') }}
                    </td>
                    <td class="py-2 table-plan-value">
                      <span v-if="data.oneTime.timeEnd">
                        {{ data.oneTime.startTime | dateTimeStamp }}
                      </span>
                      <div v-if="!data.oneTime.timeEnd">
                        <p>
                          {{ $i18nPresenter.t('Not started yet') }}
                        </p>
                        <p
                          class="mt-1 text-xs"
                        >
                          {{ $i18nPresenter.t('Your event day will start when you have more than 7 participants joining your presentation simultaneously and last for 24 hours') }}
                        </p>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="table-plan-property">
                      <i class="mr-1 far fa-calendar-check fa-fw" />
                      {{ $i18nPresenter.t('End date:') }}
                    </td>
                    <td class="py-2 table-plan-value">
                      <span v-if="data.oneTime.timeEnd">
                        {{ data.oneTime.timeEnd | dateTimeStamp }}
                      </span>
                      <div v-if="!data.oneTime.timeEnd">
                        {{ $i18nPresenter.t('24 hours since your event starts') }}
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td class="table-plan-property">
                      <i class="mr-1 fas fa-money-check-alt fa-fw" />
                      {{ $i18nPresenter.t('One-time cost:') }}
                    </td>
                    <td class="table-plan-value">
                      {{ $store.state.plan.price }} USD
                    </td>
                  </tr>
                  <tr v-if="data.oneTime.coupon">
                    <td class="align-top table-plan-property">
                      <i class="mr-1 fas fa-gift fa-fw" />
                      {{ $i18nPresenter.t('Discount applied:') }}
                    </td>
                    <td class="table-plan-value">
                      <div>
                        <span
                          :class="{'sub-has-coupon line-through pr-1': data.oneTime.coupon}"
                        >{{ $store.state.plan.price }} USD</span>
                        <span class="font-bold">{{ data.oneTime.amount }} USD</span>
                      </div>
                      <div
                        class="text-xs label-coupon text-base-80"
                      >
                        {{ $i18nPresenter.t('Promo code:') }} {{ data.oneTime.coupon.id }} - {{ data.oneTime.coupon.name }}
                      </div>
                      <div class="text-xs label-coupon text-base-80">
                        {{ $i18nPresenter.t('Duration:') }} {{ data.oneTime.coupon.duration }}
                        <span
                          v-if="data.oneTime.coupon.duration == 'repeating'"
                        >({{ data.oneTime.coupon.duration_in_months }} {{ $i18nPresenter.t('months') }})</span>
                      </div>
                    </td>
                  </tr>

                  <tr v-if="data.card">
                    <td class="align-top table-plan-property">
                      <i class="mr-1 far fa-credit-card fa-fw" />
                      {{ $i18nPresenter.t('Payment method:') }}
                    </td>
                    <td class="py-2 table-plan-value">
                      <strong class="uppercase">{{ data.card.brand }}</strong> •••• •••• ••••
                      <strong>{{ data.card.last4 }}</strong>
                      <br>
                      <span v-if="data.subscription" class="small-90">
                        <span
                          class="text-link--muted"
                          @click="showPopupUpdateCardInfo()"
                        >{{ $i18nPresenter.t('Update') }}</span> /
                      </span>
                      <span
                        class="text-link--muted"
                        @click="removeCardInfo()"
                      >{{ $i18nPresenter.t('Remove') }}</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="table-plan-property">
                      <aha-icon name="coin" class="-mt-0.5 -ml-0.5" />
                      {{ $i18nPresenter.t('Credit balance:') }}
                    </td>
                    <td
                      class="py-2 table-plan-value"
                    >
                      <span v-if="currentUser.balance">
                        {{ (0 - currentUser.balance)/100 | floatNumber }} USD
                      </span>
                      <span v-else>{{ $i18nPresenter.t('0.00 USD') }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
          </aha-section>
        </div>

        <TableCurrentPlanWithSubscription
          v-if="data.subscription"
          :data="data"
          @getPlanColor="getPlanColor"
          :isDisplayingNextInvoicePromotion="isDisplayingNextInvoicePromotion"
          :autoRenewOn="autoRenewOn"
          :isTurningOnAutoRenew="isTurningOnAutoRenew"
          @handleTurnOnAutoRenew="handleTurnOnAutoRenew"
          @showPopupUpdateCardInfo="showPopupUpdateCardInfo"
          @removeCardInfo="removeCardInfo"
          @showPromotionOnCancel="showPromotionOnCancel"
        />
        <div v-if="$store.state.plan.type == 'free'">
          <aha-section>
            <template #header>
              <h5>{{ $i18nPresenter.t('Current plan') }}</h5>
            </template>
            <template #body>
              <table class="table-plan">
                <colgroup>
                  <col class="w-1/2 md:w-1/3">
                  <col class="w-1/2 md:w-2/3">
                </colgroup>
                <tbody class="w-full">
                  <tr>
                    <td class="table-plan-property">
                      <i class="mr-1 fas fa-cart-arrow-down fa-fw" />
                      {{ $i18nPresenter.t('Your plan:') }}
                    </td>
                    <td class="py-2 font-bold text-primary-90">
                      Free
                    </td>
                  </tr>
                  <tr>
                    <td class="table-plan-property">
                      <i class="mr-1 fas fa-user fa-fw" />
                      {{ $i18nPresenter.t('Owner:') }}
                    </td>
                    <td class="py-2 table-plan-value">
                      {{ currentUser.email }}
                    </td>
                  </tr>
                  <tr>
                    <td class="table-plan-property">
                      <i class="mr-1 fas fa-users fa-fw" />
                      {{ $i18nPresenter.t('Participant limit:') }}
                    </td>
                    <td class="py-2 table-plan-value">
                      {{ $store.state.plan.limit | formatNumber }}
                    </td>
                  </tr>
                  <tr>
                    <td class="table-plan-property">
                      <aha-icon name="coin" class="-mt-0.5 -ml-0.5" />
                      {{ $i18nPresenter.t('Credit balance:') }}
                    </td>
                    <td
                      class="py-2 table-plan-value"
                    >
                      <span v-if="currentUser.balance">
                        {{ (0 - currentUser.balance)/100 | floatNumber }} USD
                      </span>
                      <span v-else>{{ $i18nPresenter.t('0.00 USD') }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
            <template
              #footer
              v-if="showUpgradePlanFooter"
            >
              <div class="h-13 p-3 bg-yellow-10 text-yellow-100">
                <span>{{ $i18nPresenter.t('Upgrading your plan here will not affect your team plan.') }}</span>
              </div>
            </template>
          </aha-section>
        </div>

        <aha-section
          style="overflow: clip"
          class="w-full"
          id="upgrade-your-plan"
          v-if="$store.state.plan && $store.state.plan.type !== 'pro-yearly' &&
            $store.state.plan.group !== 'white-label'"
        >
          <template #header>
            <h5>
              {{ $i18nPresenter.t('Upgrade your plan') }}
            </h5>
          </template>
          <template #body>
            <UserPricing :permission="permission" @back-to-non-edu="textShowPlanEdu = true" />
          </template>
        </aha-section>
        <div
          class="mb-20 text-sm text-base-80"
        >
          <div class="enterprise-cta flex flex-col md:flex-row justify-center items-center bg-base-0 h-32 md:h-20 rounded-lg mb-4 px-6">
            <span class="group-icon bg-primary-70 rounded-full w-12 h-12 mr-2 hidden md:flex justify-center items-center">
              <font-awesome-icon size="lg" icon="users" class="text-primary-90 text-2xl" />
            </span>
            <p class="text-base font-bold text-base-90 mt-4 md:mt-0 md:mr-4 mb-4 md:mb-0">
              <span v-html="$i18nPresenter.t('saveUpTo59')"></span>
            </p>
            <button
              class="cta-button mx-auto md:ml-auto uppercase p-4 mb-4 md:mb-0 md:mx-0"
              type="button"
              @click="clickToSpeakUs"
            >
              {{ $i18nPresenter.t('Find out more') }}
            </button>
          </div>
          <div
            class="py-2 title-open-edu title-open-edu-mobile"
            v-if="isTextShowPlanEdu && $store.state.plan &&
              $store.state.plan.type !== 'pro-yearly' &&
              $store.state.plan.group !== 'white-label'"
          >
            <span><b>{{ $i18nPresenter.t('Educational plans') }}</b></span>
            <br>
            {{ $i18nPresenter.t('Special rates for teachers, students and nonprofit organisations.') }}
            <a class="primary" href="#upgrade-your-plan" @click="showEduPlan()">{{ $i18nPresenter.t('Learn more') }}</a>
            <svg
              width="12"
              height="10"
              viewBox="0 0 12 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              version="1.1"
            >
              <path d="M11.7803 5.59278C12.0732 5.2648 12.0732 4.73218 11.7803 4.40421L8.78012 1.04578C8.48713 0.717812 8.01133 0.717812 7.71835 1.04578C7.42536 1.37375 7.42536 1.90638 7.71835 2.23435L9.43874 4.1602L0.750037 4.1602C0.335173 4.1602 0 4.5354 0 4.9998C0 5.46421 0.335173 5.83941 0.750037 5.83941L9.43874 5.83941L7.71835 7.76526C7.42536 8.09323 7.42536 8.62585 7.71835 8.95383C8.01133 9.2818 8.48713 9.2818 8.78012 8.95383L11.7803 5.5954V5.59278Z" fill="#348EF6" />
            </svg>
          </div>
          <p class="text-left">
            {{ $i18nPresenter.t('For other inquiries such as changing your subscription, please email us at') }}
            <a href="mailto:hi@ahaslides.com" class="text-primary-90">hi@ahaslides.com</a>.
          </p>
        </div>
      </div>

      <aha-modal
        :title="$i18nPresenter.t('Auto-renewal is turned on')"
        ref="auto-renew-confirm-popup"
        id="auto-renew-confirm-popup"
        v-if="isConfirmCancel"
        @hide="handleModalConfirmOk()"
        action-position="justify-center"
      >
        <p class="mb-3">
          {{ $i18nPresenter.t('Thank you for using AhaSlides!') }}
        </p>
        <template #action>
          <aha-button class="w-16 mb-2" @click="handleModalConfirmOk()">
            OK
          </aha-button>
        </template>
      </aha-modal>

      <!-- Modal reminder promotion on cancel subscriptionoffer -->
      <aha-modal
        :title="reminderPromotionOnCancellationTitle"
        ref="promotion-on-cancel-reminder-popup"
        id="promotion-on-cancel-reminder-popup"
        v-if="isPromotionOnCancelReminderPopupVisible"
        @hide="handleClosePromotionOnCancelReminderPopup()"
        action-position="justify-center"
        action-alignment="flex-col items-center"
        width="451px"
      >
        <img
          class="mb-6"
          :src="require('@/assets/images/cancellation-offer/3-remind.jpg')"
        />
        <p
          v-html="$i18nPresenter.t('exclusiveOfferPercentage', {
            offer: offerPercentage
          })"
        >
        </p>
        <template #action>
          <aha-button
            class="w-full mb-3"
            @click="handleClosePromotionOnCancelReminderPopup()"
          >
            {{ $i18nPresenter.t('I will stay') }}
          </aha-button>
          <aha-link
            @click="handleContinueWithCancellation()"
          >
            {{ $i18nPresenter.t('I will cancel and lose the offer') }}
          </aha-link>
        </template>
      </aha-modal>

      <modal-remove-card-info
        :current-user-plan="currentUserPlan"
        @handleRemoveCardInfo="handleRemoveCardInfo"
        @handleCancelRemoveCardInfo="handleCancelRemoveCardInfo"
        :current-subscription="data"
        v-if="isRemoveCardInfo"
      />
      <modal-update-card-info
        @handleCloseUpdateCardInfo="handleCloseUpdateCardInfo"
        v-if="showUpdateCardInfo"
      />
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import { mapState } from 'vuex'
import Config from '@/config'
import EventBus from '@/eventBus'
import Util from '@/util'
import Pricing from '@/pricing'

// UI
import Messages from '@/components/UI/Messages'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'
import InvitationNotifications from '@/components-v2/common/global-notification/InvitationNotifications.vue'
import EduYearlyNotification from '@/components-v2/common/global-notification/EduYearlyNotification.vue'
import ModalUpdateCardInfo from '../modals/UpdateCardInfo.vue'
import ModalRemoveCardInfo from '../modals/RemoveCardInfo.vue'
import PageBreadcrumb from '../common/page-breadcrumb/PageBreadcrumb.vue'
import UserPricing from '@/components-v2/my-plan/user-pricing-v2/UserPricing.vue'
import TableCurrentPlanWithSubscription from './TableCurrentPlanWithSubscription.vue'
import OnboardingV3BackArrow from '../common/onboarding/OnboardingV3BackArrow.vue'

import storageKeys from '@/constant/storageKeys'
import eventCategories from '@/constant/eventCategories'

// helpers
import { isDisplayingNextInvoicePromotion } from './userPlan.helper'
import PlanAPI from '@/api/plan'

export default {
  name: 'UserPlan',
  components: {
    ModalUpdateCardInfo,
    ModalRemoveCardInfo,
    PageBreadcrumb,
    UiMessages: Messages,
    UserPricing,
    TableCurrentPlanWithSubscription,
    InvitationNotifications,
    EduYearlyNotification,
    PublicAnnouncement,
    OnboardingV3BackArrow,
  },
  data() {
    return {
      isPresenterPlanLoading: false,
      isPromotionOnCancelReminderPopupVisible: false,
      data: {},
      typePay: 1,
      url: '',
      isTurningOnAutoRenew: false,
      dataConfirm: {},
      autoRenewOn: false,
      isConfirmCancel: false,
      isCheckout: false,
      stripe: null,
      coupon: null,
      hasErrorOnLoadingPresenterPlan: false,
      isRemoveCardInfo: false,
      showUpdateCardInfo: false,
      isRemoving: false,
      textShowPlanEdu: true,
      permission: {
        imeMini: false,
        oneTimeSmall: false,
        oneTimeMedium: false,
        oneTimeLarge: false,
        essentialMonthly: false,
        essentialYearly: false,
        plusMonthly: false,
        plusYearly: false,
        proMonthly: false,
        proYearly: false,
        eduSmall: false,
        eduMedium: false,
        eduLarge: false,
        eduSmallYearly: false,
        eduMediumYearly: false,
        eduLargeYearly: false,
      },
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
    showUpgradePlanFooter() {
      if (
        this.$store.state.currentUser.accountId
          && this.$store.state.currentUser.accountRole === 'owner'
          && this.$store.state.currentAccount.createdFrom === 'presenter') return true
      return false
    },
    abTestOpenEdu() {
      return true
    },
    isTextShowPlanEdu() {
      return this.textShowPlanEdu && this.abTestOpenEdu
    },
    isPaypalSubscription() {
      return this.data.subscription &&
        this.data.subscription.metadata && this.data.subscription.metadata.isPaypal
    },

    isDisplayingNextInvoicePromotion() {
      return isDisplayingNextInvoicePromotion(this.data)
    },

    currentUserPlan() {
      return Pricing.getPlanByType(this.currentUser.plan)
    },
    offerPercentage() {
      if (!this.currentUser.id) return null
      if (this.currentUser.id % 2 === 0) return '25%'
      return '50%'
    },
    reminderPromotionOnCancellationTitle() {
      const { firstName, lastName } = this.currentUser
      return this.$i18nPresenter.t('heyXYouStillGotYOff', {
        firstName,
        lastName,
        percent: this.offerPercentage,
      })
    },

    breadCumbText() {
      return this.$i18nPresenter.t('My Plan')
    },
  },
  watch: {
    '$store.state.currentUser': function () {
      if (this.currentUser.id) {
        this.getMyPlan()
        if (this.currentUser.stripeSource === 'australia') {
          this.stripe = window.Stripe(Config.stripePublicKey)
        } else {
          this.stripe = window.Stripe(Config.singaporeStripePublishableKey)
        }
      }
    },
  },

  mounted() {
    document.body.classList.remove('no-scroll')
    if (this.currentUser.id) {
      this.getMyPlan()

      if (this.currentUser.stripeSource === 'australia') {
        this.stripe = window.Stripe(Config.stripePublicKey)
      } else {
        this.stripe = window.Stripe(Config.singaporeStripePublishableKey)
      }
    }
    this.hideAnnoucementProcessing()
    this.url = Config.presenterDomain
    EventBus.$on('handleTurnOnAutoRenew', (notShowPopupDone) => {
      this._turnOnAutoRenew(notShowPopupDone)
    })
  },
  destroyed() {
    EventBus.$off('handleTurnOnAutoRenew')
  },
  methods: {
    hideAnnoucementProcessing() {
      if (window.localStorage.getItem('hideNewPriceAnnoucement') == 'true') this.showAnnouncementNewPlan = false
    },
    showEduPlan() {
      this.textShowPlanEdu = false
      EventBus.$emit('clickShowEduPlan')
    },
    async getMyPlan() {
      if (this.currentUser.plan === 'free') {
        this.data.plan = this.$store.state.plan
        return
      }

      this.isPresenterPlanLoading = true
      this.hasErrorOnLoadingPresenterPlan = false

      try {
        const res = await PlanAPI.getMyPlan()
        this.data = res.body

        if (this.data.subscription) {
          this.data.subscription.plan = Pricing.getPlanByType(res.body.subscription.type)
        }
      } catch (error) {
        this.hasErrorOnLoadingPresenterPlan = true
        Util.mixpanelTracking('User Plan', 'Get user plan', 'Get user plan failed')
        throw error
      } finally {
        this.isPresenterPlanLoading = false
      }

      this._checkPermissionUpgrade()
    },

    getPlanColor(plan) {
      if (!plan) return ''
      const planName = plan.name
      const lowercasePlan = planName.toLowerCase()
      if (lowercasePlan.includes('medium') || lowercasePlan.includes('plus')) {
        return 'text-green-50'
      }
      if (lowercasePlan.includes('small') || lowercasePlan.includes('essential')) {
        return 'text-yellow-50'
      }
      if (lowercasePlan.includes('pro') || lowercasePlan.includes('large')) {
        return 'text-purple-50'
      }
      if (lowercasePlan.includes('white label')) {
        return 'text-secondary-50'
      }
      return 'text-primary-90'
    },

    _checkPermissionUpgrade() {
      if (this.$store.state.plan.type === 'free') return

      if (this.$store.state.plan.type === 'one-time-mini') {
        this.permission.oneTimeMini = true
      }
      if (this.$store.state.plan.type === 'one-time-small') {
        this.permission.oneTimeSmall = true
      }
      if (this.$store.state.plan.type === 'one-time-medium') {
        this.permission.oneTimeMedium = true
        this.permission.essentialMonthly = true
      }
      if (this.$store.state.plan.type === 'one-time-large') {
        this.permission.oneTimeLarge = true
        this.permission.essentialMonthly = true
        this.permission.plusMonthly = true
      }
      if (this.$store.state.plan.type === 'edu-small-yearly') {
        this.permission.eduSmallYearly = true
      }
      if (this.$store.state.plan.type === 'edu-medium-yearly') {
        this.permission.eduSmallYearly = true
        this.permission.eduMediumYearly = true
      }
      if (this.$store.state.plan.type === 'edu-large-yearly') {
        this.permission.eduSmallYearly = true
        this.permission.eduMediumYearly = true
        this.permission.eduLargeYearly = true
      }
      if (this.$store.state.plan.type === 'essential-monthly') {
        this.permission.essentialMonthly = true
      }
      if (this.$store.state.plan.type === 'essential-yearly') {
        this.permission.essentialYearly = true
      }
      if (this.$store.state.plan.type === 'plus-monthly') {
        this.permission.plusMonthly = true
      }
      if (this.$store.state.plan.type === 'plus-yearly') {
        this.permission.plusYearly = true
      }
      if (this.$store.state.plan.type === 'pro-monthly') {
        this.permission.proMonthly = true
      }
      if (this.$store.state.plan.type === 'pro-yearly') {
        this.permission.proMonthly = true
      }

      if (this.data.subscription && this.data.oneTime) {
        this.permission = {
          essentialMonthly: true,
          essentialYearly: true,
          plusMonthly: true,
          plusYearly: true,
          proMonthly: true,
          proYearly: true,
          eduSmallYearly: true,
          eduMediumYearly: true,
          eduLargeYearly: true,
        }

        if (this.$store.state.plan.type === 'one-time-mini') {
          this.permission.oneTimeMini = true
        }
        if (this.$store.state.plan.type === 'one-time-small') {
          this.permission.oneTimeMini = true
          this.permission.oneTimeSmall = true
        }
        if (this.$store.state.plan.type === 'one-time-medium') {
          this.permission.oneTimeMini = true
          this.permission.oneTimeSmall = true
          this.permission.oneTimeMedium = true
        }
        if (this.$store.state.plan.type === 'one-time-large') {
          this.permission.oneTimeMini = true
          this.permission.oneTimeSmall = true
          this.permission.oneTimeMedium = true
          this.permission.oneTimeLarge = true
        }
        return
      }

      if (this.$store.state.plan.type === 'one-time-mini') {
        this.permission.oneTimeMini = true
      }
      if (this.$store.state.plan.type === 'one-time-small') {
        this.permission.oneTimeMini = true
        this.permission.oneTimeSmall = true
      }
      if (this.$store.state.plan.type === 'one-time-medium') {
        this.permission.oneTimeMini = true
        this.permission.oneTimeSmall = true
        this.permission.oneTimeMedium = true
      }
      if (this.$store.state.plan.type === 'one-time-large') {
        this.permission.oneTimeMini = true
        this.permission.oneTimeSmall = true
        this.permission.oneTimeMedium = true
        this.permission.oneTimeLarge = true
      }

      if (this.data.subscription && !this.data.oneTime) {
        const subPlan = Pricing.getPlanByType(this.data.subscription.type)
        Pricing.plans.forEach((item) => {
          if (item.price < this.data.subscription.credit) {
            if (item.type === 'essential-monthly') {
              this.permission.essentialMonthly = true
            }
            if (item.type === 'essential-yearly') {
              this.permission.essentialYearly = true
            }
            if (item.type === 'plus-monthly') {
              this.permission.plusMonthly = true
            }
            if (item.type === 'plus-yearly') {
              this.permission.plusYearly = true
            }
            if (item.type === 'pro-monthly') {
              this.permission.proMonthly = true
            }
            if (item.type === 'pro-yearly') {
              this.permission.proYearly = true
            }
            if (item.type === 'edu-small-yearly') {
              this.permission.eduSmallYearly = true
            }
            if (item.type === 'edu-medium-yearly') {
              this.permission.eduMediumYearly = true
            }
            if (item.type === 'edu-large-yearly') {
              this.permission.eduLargeYearly = true
            }
          }
        })

        if (subPlan.group !== 'edu' && subPlan.billingCycle === 'yearly') {
          this.permission.eduSmallYearly = true
          this.permission.eduMediumYearly = true
          this.permission.eduLargeYearly = true
        }

        if (subPlan.group === 'essential') {
          this.permission.oneTimeMini = true
          this.permission.oneTimeSmall = true
        }
        if (subPlan.group === 'plus') {
          this.permission.oneTimeMini = true
          this.permission.oneTimeSmall = true
          this.permission.oneTimeMedium = true
        }
        if (subPlan.group === 'pro') {
          this.permission.oneTimeMini = true
          this.permission.oneTimeSmall = true
          this.permission.oneTimeMedium = true
          this.permission.oneTimeLarge = true
        }
        if (subPlan.type === 'edu-small') {
          this.permission.oneTimeMini = true
        }
        if (subPlan.type === 'edu-medium') {
          this.permission.oneTimeMini = true
          this.permission.oneTimeSmall = true
        }
        if (subPlan.type === 'edu-large') {
          this.permission.oneTimeMini = true
          this.permission.oneTimeSmall = true
          this.permission.oneTimeMedium = true
        }

        return
      }

      if (!this.data.subscription && this.data.oneTime) {
        if (this.$store.state.plan.type === 'one-time-mini') {
          this.permission.oneTimeMini = true
        }
        if (this.$store.state.plan.type === 'one-time-small') {
          this.permission.oneTimeMini = true
          this.permission.oneTimeSmall = true
        }
        if (this.$store.state.plan.type === 'one-time-medium') {
          this.permission.oneTimeMini = true
          this.permission.oneTimeSmall = true
          this.permission.oneTimeMedium = true
        }
        if (this.$store.state.plan.type === 'one-time-large') {
          this.permission.oneTimeMini = true
          this.permission.oneTimeSmall = true
          this.permission.oneTimeMedium = true
          this.permission.oneTimeLarge = true
        }
        Pricing.plans.forEach((item) => {
          if (item.price < this.data.oneTime.price) {
            if (item.type === 'essential-monthly') {
              this.permission.essentialMonthly = true
            }
            if (item.type === 'essential-yearly') {
              this.permission.essentialYearly = true
            }
            if (item.type === 'plus-monthly') {
              this.permission.plusMonthly = true
            }
            if (item.type === 'plus-yearly') {
              this.permission.plusYearly = true
            }
            if (item.type === 'pro-monthly') {
              this.permission.proMonthly = true
            }
            if (item.type === 'pro-yearly') {
              this.permission.proYearly = true
            }
            if (item.type === 'edu-small-yearly') {
              this.permission.eduSmallYearly = true
            }
            if (item.type === 'edu-medium-yearly') {
              this.permission.eduMediumYearly = true
            }
            if (item.type === 'edu-large-yearly') {
              this.permission.eduLargeYearly = true
            }
          }
        })
      }
    },
    clickToSpeakUs() {
      Util.mixpanelTracking('My plan > White label', 'PV > WhiteLabel', 'White label')
      window.open('https://form.typeform.com/to/j2O9GXjI/?campaign=my-plan', '_blank')
    },
    changeTypePay(index) {
      this.typePay = index
    },

    handleTurnOnAutoRenew() {
      if (!this.data.card) {
        this.showUpdateCardInfo = true
      } else {
        this._turnOnAutoRenew()
      }
    },

    _turnOnAutoRenew(notShowPopupDone) {
      // API turn on auto renew
      if (this.isTurningOnAutoRenew) return
      this.isTurningOnAutoRenew = true
      this.$http
        .post(Config.api.changeSubscription, {
          stop: false,
        })
        .then(() => {
          this.isTurningOnAutoRenew = false
          this.data.subscription.cancel_at_period_end = false
          Util.mixpanelTracking(
            'User Plan',
            'User Turn on Auto-renewal',
            'User Turn on Auto-renewal Success',
          )
          if (notShowPopupDone) return
          this.dataConfirm = {
            title: 'Auto-renewal is turned on',
            description: 'Thank you for using AhaSlides!',
            noCancelBtn: true,
          }
          this.isConfirmCancel = true
        })
    },
    handleModalConfirmOk() {
      this.isConfirmCancel = false
      this.isCancelledSubscription = false
    },

    handleClosePromotionOnCancelReminderPopup() {
      Util.mixpanelTracking(
        eventCategories.MY_PLAN,
        'Auto-Offer on cancellation > Offer reminder > I"ll stay',
        `Offer: ${this.offerPercentage}`,
      )
      this.isPromotionOnCancelReminderPopupVisible = false
    },

    handleContinueWithCancellation() {
      this.isPromotionOnCancelReminderPopupVisible = false
      localStorage.setItem(storageKeys.IS_REMOVE_CARD_PURPOSE, true)
      this.$router.push({ name: 'UserCancelSub' })
    },

    removeCardInfo() {
      if (this.isDisplayingNextInvoicePromotion) {
        this.isPromotionOnCancelReminderPopupVisible = true
        return
      }
      // New plan: not show promote modal, redirect to cancel page
      // Old plan: show promote modal
      const oldPlan = Util.isSubscribleOldPlan(this.currentUser.plan)
      if (!oldPlan) {
        localStorage.setItem(storageKeys.IS_REMOVE_CARD_PURPOSE, true)
        setTimeout(() => {
          this.$router.push({ name: 'UserCancelSub' })
        }, 500)
      } else {
        this.isRemoveCardInfo = true
      }
    },
    showPromotionOnCancel() {
      this.isPromotionOnCancelReminderPopupVisible = true
    },
    async handleRemoveCardInfo() {
      this.isRemoveCardInfo = false
      if (this.data.subscription) {
        this.data.subscription.cancel_at_period_end = true
      }
      this.autoRenewOn = false
      this.data.card = null
      Util.mixpanelTracking(
        'User Plan',
        'User remove card info',
        'User click on OK remove card info',
      )
    },

    handleCancelRemoveCardInfo() {
      this.isRemoveCardInfo = false
      Util.mixpanelTracking(
        'User Plan',
        'User remove card info',
        'User click on cancel remove card info',
      )
    },

    showPopupUpdateCardInfo() {
      Util.mixpanelTracking(
        'User Plan',
        'User update card info',
        'User click on update card info',
      )
      this.showUpdateCardInfo = true
    },

    handleCloseUpdateCardInfo(card) {
      this.isTurningOnAutoRenew = false
      this.showUpdateCardInfo = false
      if (card) {
        this.data.card = card
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.my-plan {
  @apply p-4 md:px-10 md:py-6 relative;
}

.my-plan-content {
  @apply w-full lg:w-11/12 flex flex-col justify-center md:mx-auto;
  max-width: 1280px;
}

.announcement {
  @apply flex flex-row items-center py-2.5 px-4 my-6 gap-2 rounded-lg relative;
  background: #D3E6FD;
  span {
    @apply mr-4;
  }
  b {
    font-weight: 700;
    color: black;
    text-decoration-line: underline;
  }
  i {
    color: rgba(40, 46, 62, 1);
  }
}
.table-plan {
  @apply w-full;
}

.table-plan-property {
  @apply font-bold py-2 w-1/2 md:w-1/3 align-top;
}

.table-plan-value {
  @apply py-2 w-1/2 md:w-2/3 align-top;
  word-break: break-word;
}

@media (min-width: 1281px) {
  .title-open-edu-mobile{
    display: none;
  }
}

.title-open-edu-mobile{
  span{
    b{
      font-style: normal;
      font-weight: 700;
      font-size: 16px;
      line-height: 22px;
    }
  }

  svg{
    display: inline-block;
    vertical-align: baseline;
  }

  a {
    font-weight: 700;
    font-size: 14px;
  }
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  margin-bottom: 25px;
}

.enterprise-cta {
  .group-icon {
    min-width: 48px;
  }
}

.cta-button {
  border-radius: 20px;
  border: 0;
  min-width: 160px;
  color: #fff;
  cursor: pointer;
  letter-spacing: 1px;
  text-transform: uppercase;
  background-color: #348EF6;
  transition: 0.15s linear;
  height: 34px;
  padding: 0 20px;
  font-size: 0.8rem;
  line-height: 0;
  &:hover {
    background-color: #1178ee;
  }
}

.cta-button:focus {
  outline: none;
}

</style>
